import { EventPayload, GemSalesConfig, IntentProfile, PilotMetricItem, PilotSummary, TemplateReadinessSummary } from "./types";
import { listExperienceTemplateReadiness } from "./experiences";

const OBJECTION_KEYS = new Set(["hesitation", "concern", "proof_topic", "story_angle"]);

/** True for a canonical event name or the pre-rename `gemchat_*` spelling of the same event. */
function isEvent(eventName: string, canonical: string): boolean {
  return eventName === canonical || eventName === canonical.replace("gemsales_", "gemchat_");
}

export function buildPilotSummary(events: EventPayload[], intentProfile: IntentProfile): PilotSummary {
  const totals = {
    events: events.length,
    triggerImpressions: countEvents(events, "gemsales_slot_impression"),
    triggerClicks: countEvents(events, "gemsales_opened"),
    flowStarts: countEvents(events, "gemsales_quiz_started"),
    flowCompletions: countEvents(events, "gemsales_recommendation_generated"),
    recommendationViews: countEvents(events, "gemsales_recommendation_viewed"),
    experienceViews: countEvents(events, "gemsales_experience_viewed"),
    experienceSectionClicks: countEvents(events, "gemsales_experience_section_clicked"),
    experienceGuideClicks: countEvents(events, "gemsales_experience_guide_clicked"),
    productClicks: countEvents(events, "gemsales_product_clicked"),
    assistedAddToCarts: countEvents(events, "gemsales_add_to_cart_clicked"),
    // Generative-surface funnel (P3·3): opened → rendered → add-to-cart (the surface CTA is an ATC).
    // These are the buildable attribution counts; a live order pixel later joins on surfaceId.
    surfaceOpens: countEvents(events, "gemsales_surface_opened"),
    surfaceRenders: countEvents(events, "gemsales_surface_rendered"),
    surfaceAddToCarts: countEvents(events, "gemsales_surface_add_to_cart"),
    surfaceFollowups: countEvents(events, "gemsales_surface_followup"),
  };

  return {
    totals,
    rates: {
      triggerClickRate: rate(totals.triggerClicks, totals.triggerImpressions),
      flowCompletionRate: rate(totals.flowCompletions, totals.flowStarts),
      recommendationClickRate: rate(totals.productClicks, totals.recommendationViews),
      assistedAddToCartRate: rate(totals.assistedAddToCarts, totals.productClicks),
      // How often a generated surface actually paints, and how often a painted surface drives an add-to-cart.
      surfaceRenderRate: rate(totals.surfaceRenders, totals.surfaceOpens),
      surfaceAssistRate: rate(totals.surfaceAddToCarts, totals.surfaceRenders),
    },
    topSlots: topValues(events, "slot_id"),
    topFlows: topValues(events, "flow_id"),
    topExperienceTemplates: topValues(events, "template_id"),
    topIntents: topIntentScores(intentProfile),
    topObjections: topObjections(events),
  };
}

export function buildPilotSummaryWithTemplateReadiness(
  events: EventPayload[],
  intentProfile: IntentProfile,
  config: GemSalesConfig,
): PilotSummary {
  return {
    ...buildPilotSummary(events, intentProfile),
    templateReadiness: buildTemplateReadinessSummary(config),
  };
}

export function buildTemplateReadinessSummary(config: GemSalesConfig): TemplateReadinessSummary {
  const readiness = listExperienceTemplateReadiness(config);
  const blockedTemplates = readiness.filter((item) => !item.ready).map((item) => item.templateId);
  const warningTemplates = readiness.filter((item) => item.warnings.length > 0).map((item) => item.templateId);

  return {
    ready: readiness.length - blockedTemplates.length,
    blocked: blockedTemplates.length,
    warnings: warningTemplates.length,
    blockedTemplates,
    warningTemplates,
  };
}

/**
 * Count a canonical event, including rows stored under its pre-rename name. Events recorded before
 * the GemChat → GemSales rename sit in the log as `gemchat_*`; without this bridge every metric
 * would appear to reset to zero on the day of the rename, which is a reporting lie, not a rename.
 */
function countEvents(events: EventPayload[], eventName: string): number {
  const legacy = eventName.startsWith("gemsales_") ? eventName.replace("gemsales_", "gemchat_") : null;
  return events.filter((event) => event.eventName === eventName || (legacy !== null && event.eventName === legacy)).length;
}

function rate(numerator: number, denominator: number): number {
  if (denominator <= 0) return 0;

  return round(numerator / denominator);
}

function topValues(events: EventPayload[], propertyName: string, limit = 5): PilotMetricItem[] {
  const counts = new Map<string, number>();

  events.forEach((event) => {
    const value = event.properties[propertyName];
    if (typeof value !== "string" || !value) return;
    counts.set(value, (counts.get(value) ?? 0) + 1);
  });

  return sortCounts(counts).slice(0, limit);
}

function topIntentScores(intentProfile: IntentProfile, limit = 5): PilotMetricItem[] {
  return Object.entries(intentProfile.scores)
    .filter(([, score]) => Number.isFinite(score) && score > 0)
    .sort(([, a], [, b]) => b - a)
    .slice(0, limit)
    .map(([key, score]) => ({ key, count: Math.round(score * 100) }));
}

function topObjections(events: EventPayload[], limit = 5): PilotMetricItem[] {
  const counts = new Map<string, number>();

  events
    .forEach((event) => {
      if (isEvent(event.eventName, "gemsales_followup_submitted")) {
        const topic = event.properties.answer_topic;
        if (typeof topic !== "string" || !topic) return;
        const key = `followup:${topic}`;
        counts.set(key, (counts.get(key) ?? 0) + 1);
        return;
      }

      if (!isEvent(event.eventName, "gemsales_answer_submitted")) return;
      const stepId = event.properties.step_id;
      const answerKey = event.properties.answer_key;
      if (typeof stepId !== "string" || typeof answerKey !== "string") return;
      if (!OBJECTION_KEYS.has(stepId)) return;

      const key = `${stepId}:${answerKey}`;
      counts.set(key, (counts.get(key) ?? 0) + 1);
    });

  return sortCounts(counts).slice(0, limit);
}

function sortCounts(counts: Map<string, number>): PilotMetricItem[] {
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([key, count]) => ({ key, count }));
}

function round(value: number): number {
  return Math.round(value * 1000) / 1000;
}
